import { restClient } from '@/api/client'
import { GRAPHQL_URL } from '@/shared/constants'
import type { SchoolPermissionGrant } from './roleV2Service'

// Roles & permissions for the SCHOOL dashboard — the `…ForSchool` GraphQL operations, called with
// the school admin's own token. Same method shapes as `schoolAdminRoleV2Service` so the UI and
// hooks can swap between the two.

async function gql<T = any>(query: string, input: Record<string, unknown>, op: string): Promise<T> {
  const res = await restClient.post(GRAPHQL_URL, { query, variables: { input } }).then(r => r.data)
  if (res?.errors?.length) throw new Error(res.errors.map((e: { message?: string }) => e.message).filter(Boolean).join('\n') || 'Request failed')
  return res?.data?.[op]
}

const list = (query: string, op: string, extra: Record<string, unknown> = {}) =>
  gql(query, { page: 1, limit: 200, ...extra }, op)

const Q_ROLES = 'query ($input: AdminGetRolesForSchoolInput) {\n  adminGetRolesForSchool(input: $input)\n}'
const Q_PERMS = 'query ($input: AdminGetPermissionsForSchoolInput) {\n  adminGetPermissionsForSchool(input: $input)\n}'
const Q_PERM_MAP = 'query ($input: AdminGetRolePermissionMappingListForSchoolInput) {\n  adminRolePermissionMappingListForSchool(input: $input)\n}'
const Q_USER_MAP = 'query ($input: AdminGetRoleUserMappingListForSchoolInput) {\n  adminRoleUserMappingListForSchool(input: $input)\n}'
const M_CREATE_ROLE = 'mutation ($input: AdminCreateRoleForSchoolInput!) {\n  adminCreateRoleForSchool(input: $input)\n}'
const M_PERM_MAP = 'mutation ($input: AdminRolePermissionMappingForSchoolInput!) {\n  adminRolePermissionMappingForSchool(input: $input)\n}'
const M_USER_MAP = 'mutation ($input: AdminRoleUserMappingForSchoolInput!) {\n  adminRoleUserMappingForSchool(input: $input)\n}'

export const schoolRoleService = {
  list: () => list(Q_ROLES, 'adminGetRolesForSchool'),
  create: (d: { name: string; description?: string }) => gql(M_CREATE_ROLE, d, 'adminCreateRoleForSchool'),

  // Every permission (the API caps a page at 200).
  listAllPermissions: async () => {
    const rows: unknown[] = []
    for (let page = 1; page <= 25; page++) {
      const res: any = await gql(Q_PERMS, { page, limit: 200 }, 'adminGetPermissionsForSchool')
      const batch: unknown[] = Array.isArray(res) ? res : (res?.data ?? [])
      rows.push(...batch)
      if (batch.length < 200) break
    }
    return rows
  },
  permissionMapList: (roleId: number) => list(Q_PERM_MAP, 'adminRolePermissionMappingListForSchool', { role_id: roleId }),
  mapPermissions: (d: { user_id?: number; rolePermissioMapping: SchoolPermissionGrant[] }) =>
    gql(M_PERM_MAP, d, 'adminRolePermissionMappingForSchool'),

  userMappingList: (p?: { user_id?: number; role_id?: number }) => list(Q_USER_MAP, 'adminRoleUserMappingListForSchool', { ...p }),
  mapUsers: (d: { role_id: number[]; user_id: number[] }) => gql(M_USER_MAP, d, 'adminRoleUserMappingForSchool'),
}
